import idb from '@/utils/idb'
import GradeAPI from '@/apis/business/common/grade-api'
import SubjectAPI from '@/apis/business/common/subject-api'
import { HttpStatusCode } from '@/common/constant'
import idbKeys from '@/common/idb-key'
import type { HttpResponse, Grade, Subject } from '@/common/entity.types'
import CacheTime from './cache-time'
import { destrucSchoolLevel } from '@/common/common-fn'
import { isNumber } from '@/components/utils/util'
import Common from '@/locales/vi/common/common.js'

const ALL_SCHOOL_LEVEL = -1
const ALL_GRADE = -1
const ARRAY_SCHOOL_LEVEL = [1, 2, 3]
const ARRAY_GRADE = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

class Dictionary {
  /**
   * @description Lấy danh sách khối
   * @param schoolLevel Cấp học, không truyền là lấy hết
   * @returns
   * @author PHNam
   */
  public async GetGrades (schoolLevel?: number | null): Promise<Grade[]> {
    const key = idbKeys.Grade
    const isExpired = await CacheTime.IsExpiredTime(key)
    let grades: Grade[] | any[] = isExpired ? [] : await idb.get(key, [])

    if (grades.length === 0) { // doesn't exist on indexeddb
      // @ts-ignore
      const response: HttpResponse = await GradeAPI.getGrade()

      if (response.IsSuccess && response.Code === HttpStatusCode.OK) {
        grades = response.Data
        await idb.set(key, grades)
        await CacheTime.SetCacheTime(key)
      }
    }

    const filteredGrades: Grade[] = schoolLevel && Array.isArray(grades)
      ? grades.filter((grade: Grade) => this.FilterGrade(grade, schoolLevel))
      : grades

    return filteredGrades ? filteredGrades?.sort(function (a, b) {
      return a.GradeID - b.GradeID
    }) : filteredGrades
  }

  /**
   * @description Kiểm tra khối theo cấp học tương ứng
   * @param subject Khối cần kiểm tra
   * @param schoolLevel Cấp học
   * @returns
   * @author PHNam
   */
  private FilterGrade (grade: Grade, schoolLevel: number): boolean {
    const schoolLevels = destrucSchoolLevel(schoolLevel)

    return schoolLevels.includes(grade.SchoolLevel)
  }

  /**
   * @description Lấy danh sách môn học theo (cấp và khối)
   * @param schoolLevel Cấp học
   * @param gradeID Khối
   * @returns
   * @author PHNam
   */
  public async GetSubjects (schoolLevel?: number | undefined, gradeID?: number | undefined, getAll?: Boolean | true): Promise<Subject[]> {
    const key = idbKeys.Subject
    const isExpired = await CacheTime.IsExpiredTime(key)

    let subjects: Subject[] | any[] = isExpired ? [] : await idb.get(key, [])
    if (subjects.length === 0) { // doesn't exist on indexeddb
      // @ts-ignore
      const response: HttpResponse = await SubjectAPI.getSubject()

      if (response.IsSuccess && response.Code === HttpStatusCode.OK) {
        subjects = response.Data
        await idb.set(key, subjects)
        await CacheTime.SetCacheTime(key)
      }
    }
    const filteredSubjects: Subject[] = Array.isArray(subjects)
      ? subjects.filter((subject: Subject) => this.FilterSubject(subject, schoolLevel, gradeID))
      : subjects

    if ((getAll === undefined || getAll === true) && filteredSubjects.length > 0) {
      const allSubject : any = {
        Id: '',
        SubjectCode: '', // Mã môn
        SubjectName: Common.Common.AllSubject, // Tên môn
        SchoolLevels: ARRAY_SCHOOL_LEVEL, // Danh sách cấp áp ụng
        Grades: ARRAY_GRADE, // Danh sách lớp học áp dụng
        SortOrder: 0, // STT
        IsSystem: true, // Dữ liệu hệ thống
        CoverImage: undefined // Ảnh
      }
      filteredSubjects.splice(0, 0, allSubject)
    }
    return filteredSubjects
  }

  /**
   * @description Kiểm tra môn học thõa mãn theo cấp học và khối tương ứng
   * @param subject Môn học cần kiểm tra
   * @param schoolLevel Cấp học
   * @param gradeID Khối
   * @returns
   * @author PHNam
   */
  private FilterSubject (subject: Subject, schoolLevel?: number | undefined, gradeID?: number | undefined): boolean {
    const hasSchoolLevel = typeof schoolLevel === 'number'
    const hasGrade = typeof gradeID === 'number'
    const schoolLevels = hasSchoolLevel ? destrucSchoolLevel(schoolLevel as number) : []
    const intersecSchoolLevel = hasSchoolLevel ? schoolLevels.filter((schoolLevel) => subject.SchoolLevels.includes(schoolLevel)) : []
    const containSchoolLevel: boolean =
    !schoolLevel ||
    subject.SchoolLevels.includes(ALL_SCHOOL_LEVEL) ||
    intersecSchoolLevel.length > 0

    const containGrade = !hasGrade || subject.Grades.includes(ALL_GRADE) || subject.Grades.includes(gradeID as number)

    return containSchoolLevel && containGrade
  }

  /**
   * @description Lấy danh sách môn học theo list subject code
   * @author NTQuy
   * @param subjectCodes danh sách subjectCode
   * @returns
   */
  public async GetSubjectsBySubjectCode (subjectCodes: String[]) {
    const key = idbKeys.Subject
    const isExpired = await CacheTime.IsExpiredTime(key)

    let subjects: Subject[] | any[] = isExpired ? [] : await idb.get(key, [])

    if (subjects.length === 0) { // doesn't exist on indexeddb
      // @ts-ignore
      const response: HttpResponse = await SubjectAPI.getSubject()

      if (response.IsSuccess && response.Code === HttpStatusCode.OK) {
        subjects = response.Data
        await idb.set(key, subjects)
        await CacheTime.SetCacheTime(key)
      }
    }

    // const filteredSubjects = subjects.filter((subject: Subject) => subjectCodes.includes(subject.SubjectCode))

    return subjects.filter((subject: Subject) => subjectCodes.includes(subject.SubjectCode))
  }

  // private FilterSubjectByClassCode (subjectCodes: A) {

  // }
}

export default new Dictionary()
